import type { TRouteObject } from "@ch/helpers/utils/types";
import ErrorPage from "@ch/pages/ErrorPage";
import MenuPageLazy from "@ch/pages/MenuPage";
import BiometricsPageLazy from "@ch/pages/MenuPage/single-pages/BiometricsPage";
import EmailPhoneUpdatePageLazy from "@ch/pages/MenuPage/single-pages/Email&PhoneNumberPage";
import PasswordUpdatePageLazy from "@ch/pages/MenuPage/single-pages/PasswordUpdatePage";
import PaymentHistoryPageLazy from "@ch/pages/MenuPage/single-pages/PaymentHistoryPage";
import ProfileInformationPageLazy from "@ch/pages/MenuPage/single-pages/ProfileInformationPage";
import SecurityPageLazy from "@ch/pages/MenuPage/single-pages/SecurityPage";
import SettingsPageLazy from "@ch/pages/MenuPage/single-pages/SettingsPage";
import TwoFaPageLazy from "@ch/pages/MenuPage/single-pages/TwoFaPage";
import NotFoundPage from "@ch/pages/NotFoundPage";
import FeedbackPageLazy from "@ch/pages/MenuPage/single-pages/FeedbackPage";
import AutoLogoutPageLazy from "@ch/pages/MenuPage/single-pages/AutoLogoutPage";
import ReferralsPageLazy from "@ch/pages/ReferralsPage";
import ReferAFriendPageLazy from "@ch/pages/ReferAFriendPage";
import MyCareTeamPageLazy from "@ch/pages/MenuPage/single-pages/MyCareTeamPage";
import MyDevicesPageLazy from "@ch/pages/MyDevicesPage";
import FeatureFlagRouteGate from "@ch/components/Gate/FeatureFlagRouteGate.tsx";
import postHogFeatureFlagKeys from "@ch/helpers/utils/postHogAnalytics/postHogFeatureFlagKeys.ts";
import {
  ReferralsPathsEnum,
  ReferralsRoutesEnum,
} from "@ch/router/ReferralsRoutes/enums";
import {
  ReferAFriendPathsEnum,
  ReferAFriendRoutesEnum,
} from "@ch/router/ReferAFriendRoutes/enums";
import {
  MyCareTeamPathsEnum,
  MyCareTeamRoutesEnum,
} from "@ch/router/MyCareTeamRoutes/enums";
import {
  MyDevicesPathsEnum,
  MyDevicesRoutesEnum,
} from "@ch/router/MyDevicesRoutes/enums";
import { MenuPathsEnum, MenuRoutesEnum } from "./enums";

const MenuRoutes: TRouteObject[] = [
  {
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <MenuPageLazy />,
      },
      {
        path: MenuPathsEnum.PROFILE_INFORMATION,
        element: <ProfileInformationPageLazy />,
        handle: {
          meta: () => ({
            title: "Profile Information",
          }),
          crumb: () => ({
            title: "Profile Information",
            route: MenuRoutesEnum.PROFILE_INFORMATION,
          }),
        },
      },
      {
        path: MenuPathsEnum.PAYMENT_HISTORY,
        element: <PaymentHistoryPageLazy />,
        handle: {
          meta: () => ({
            title: "Payment History",
          }),
          crumb: () => ({
            title: "Payment History",
            route: MenuRoutesEnum.PAYMENT_HISTORY,
          }),
        },
      },
      {
        path: ReferralsPathsEnum.ROOT,
        element: <ReferralsPageLazy />,
        handle: {
          meta: () => ({
            title: "Referrals",
          }),
          crumb: () => ({
            title: "Referrals",
            route: ReferralsRoutesEnum.ROOT,
          }),
        },
      },
      {
        path: MyCareTeamPathsEnum.ROOT,
        element: <MyCareTeamPageLazy />,
        handle: {
          meta: () => ({
            title: "My Care Team",
          }),
          crumb: () => ({
            title: "My Care Team",
            route: MyCareTeamRoutesEnum.ROOT,
          }),
        },
      },
      {
        path: MyDevicesPathsEnum.ROOT,
        element: (
          <FeatureFlagRouteGate
            featureFlagName={postHogFeatureFlagKeys.CORSANO_METRICS}
          >
            <MyDevicesPageLazy />
          </FeatureFlagRouteGate>
        ),
        handle: {
          meta: () => ({
            title: "My Devices",
          }),
          crumb: () => ({
            title: "My Devices",
            route: MyDevicesRoutesEnum.ROOT,
          }),
        },
      },
      {
        path: ReferAFriendPathsEnum.ROOT,
        element: <ReferAFriendPageLazy />,
        handle: {
          meta: () => ({
            title: "Refer a Friend",
          }),
          crumb: () => ({
            title: "Refer a Friend",
            route: ReferAFriendRoutesEnum.ROOT,
          }),
        },
      },
      {
        path: MenuPathsEnum.SETTINGS,
        element: <SettingsPageLazy />,
        handle: {
          meta: () => ({
            title: "Settings",
          }),
          crumb: () => ({
            title: "Settings",
            route: MenuRoutesEnum.SETTINGS,
          }),
        },
      },
      {
        path: MenuPathsEnum.SECURITY,
        handle: {
          meta: () => ({
            title: "Security",
          }),
          crumb: () => ({
            title: "Security",
            route: MenuRoutesEnum.SECURITY,
          }),
        },
        children: [
          {
            index: true,
            element: <SecurityPageLazy />,
          },
          {
            path: MenuPathsEnum.EMAIL_PHONE,
            element: <EmailPhoneUpdatePageLazy />,
            handle: {
              meta: () => ({
                title: "Email & Phone",
                pageTitle: "Security - Email & Phone",
              }),
              crumb: () => ({
                title: "Email & Phone",
                route: MenuRoutesEnum.EMAIL_PHONE,
              }),
            },
          },
          {
            path: MenuPathsEnum.PASSWORD_UPDATE,
            element: <PasswordUpdatePageLazy />,
            handle: {
              meta: () => ({
                title: "Password Update",
                pageTitle: "Security - Password Update",
              }),
              crumb: () => ({
                title: "Password Update",
                route: MenuRoutesEnum.PASSWORD_UPDATE,
              }),
            },
          },
          {
            path: MenuPathsEnum.AUTO_LOGOUT,
            element: <AutoLogoutPageLazy />,
            handle: {
              meta: () => ({
                title: "Auto logout",
                pageTitle: "Security - Auto Logout",
              }),
              crumb: () => ({
                title: "Auto Logout",
                route: MenuRoutesEnum.AUTO_LOGOUT,
              }),
            },
          },
          {
            path: MenuPathsEnum.BIOMETRIC,
            element: <BiometricsPageLazy />,
            handle: {
              meta: () => ({
                title: "Biometrics",
                pageTitle: "Security - Biometrics",
              }),
              crumb: () => ({
                title: "Biometrics",
                route: MenuRoutesEnum.BIOMETRIC,
              }),
            },
          },
          {
            path: MenuPathsEnum["2FA_AUTH"],
            element: <TwoFaPageLazy />,
            handle: {
              meta: () => ({
                title: "2FA",
                pageTitle: "Security - 2FA",
              }),
              crumb: () => ({
                title: "2FA",
                route: MenuRoutesEnum["2FA_AUTH"],
              }),
            },
          },
        ],
      },
      {
        path: MenuPathsEnum.FEEDBACK,
        element: <FeedbackPageLazy />,
        handle: {
          meta: () => ({
            title: "Feedback",
          }),
          crumb: () => ({
            title: "Feedback",
            route: MenuRoutesEnum.FEEDBACK,
          }),
        },
      },

      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];

export default MenuRoutes;
